/**
 * DV-STAGE-003: Content Redaction Utility
 * Feature-flagged content redaction with transport-level secret masking
 */

export interface RedactionOptions {
  enabled: boolean;
  secretKeys?: string[];
  maxValueLength?: number;
  preserveLength?: boolean;
}

export interface RedactionResult<T = unknown> {
  redacted: T;
  maskedKeys: string[];
  redactionEnabled: boolean;
}

// Secret-like keys that should always be masked (transport-level security)
const ALWAYS_MASKED_KEYS = [
  'api_key', 'apikey', 'api-key',
  'bearer', 'authorization', 'auth',
  'cookie', 'cookies', 'session', 'sessionid',
  'secret', 'password', 'pwd', 'pass',
  'token', 'access_token', 'refresh_token',
  'key', 'private_key', 'public_key',
  'credential', 'credentials',
  'signature', 'hash', 'digest'
];

// Content that should be redacted when feature flag is enabled
const REDACTABLE_KEYS = [
  'email', 'phone', 'address', 'name',
  'user_id', 'userId', 'user-id',
  'ip', 'ip_address', 'ipaddress',
  'location', 'coordinates', 'lat', 'lng',
  'prompt', 'content', 'text', 'message',
  'response', 'output', 'result'
];

/**
 * Check if a key should always be masked (transport-level security)
 */
function isSecretKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return ALWAYS_MASKED_KEYS.some(secretKey =>
    lowerKey.includes(secretKey) || secretKey.includes(lowerKey)
  );
}

/**
 * Check if a key should be redacted when feature flag is enabled
 */
function isRedactableKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return REDACTABLE_KEYS.some(redactKey =>
    lowerKey.includes(redactKey) || redactKey.includes(lowerKey)
  );
}

/**
 * Mask a value while preserving its type and optionally length
 */
function maskValue(value: any, preserveLength: boolean = true): string {
  if (value === null || value === undefined) {
    return String(value);
  }

  const stringValue = String(value);

  if (!preserveLength || stringValue.length <= 4) {
    return '[REDACTED]';
  }

  // Show first and last characters for context
  const visibleChars = Math.min(2, Math.floor(stringValue.length / 4));
  const maskedLength = stringValue.length - (visibleChars * 2);

  return `${stringValue.slice(0, visibleChars)}${'*'.repeat(maskedLength)}${stringValue.slice(-visibleChars)}`;
}

/**
 * Recursively redact content based on rules and feature flag
 */
function redactObject(
  obj: any,
  options: RedactionOptions,
  maskedKeys: string[] = [],
  path: string = ''
): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item, index) =>
      redactObject(item, options, maskedKeys, `${path}[${index}]`)
    );
  }

  // Handle primitive types
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle objects
  const redacted: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    const keyPath = path ? `${path}.${key}` : key;

    // Always mask secret keys (transport-level security)
    if (isSecretKey(key)) {
      redacted[key] = maskValue(value, options.preserveLength);
      maskedKeys.push(keyPath);
      continue;
    }

    // Redact content keys only if feature flag is enabled
    if (options.enabled && isRedactableKey(key)) {
      redacted[key] = maskValue(value, options.preserveLength);
      maskedKeys.push(keyPath);
      continue;
    }

    // Recursively process nested objects
    redacted[key] = redactObject(value, options, maskedKeys, keyPath);
  }

  return redacted;
}

/**
 * Main redaction function with feature flag support
 */
export function redactContent<T = unknown>(
  content: T,
  options: Partial<RedactionOptions> = {}
): RedactionResult<T> {
  const fullOptions: RedactionOptions = {
    enabled: false,
    secretKeys: [],
    maxValueLength: 1000,
    preserveLength: true,
    ...options
  };

  const maskedKeys: string[] = [];

  // Always perform transport-level secret masking
  // Additional redaction only when feature flag is enabled
  const redacted = redactObject(content, fullOptions, maskedKeys);

  return {
    redacted,
    maskedKeys,
    redactionEnabled: fullOptions.enabled
  };
}

/**
 * Redact content for logging (always enabled for secrets, feature-flagged for content)
 */
export function redactForLogging<T = unknown>(
  content: T,
  redactionEnabled: boolean = false
): RedactionResult<T> {
  return redactContent(content, {
    enabled: redactionEnabled,
    preserveLength: false // Don't preserve length in logs for security
  });
}

/**
 * Redact content for status reports (size-limited and feature-flagged)
 */
export function redactForStatusReport(
  content: any,
  redactionEnabled: boolean = false,
  maxSizeKB: number = 8
): RedactionResult {
  const result = redactContent(content, {
    enabled: redactionEnabled,
    preserveLength: true
  });

  // Truncate if too large
  const jsonString = JSON.stringify(result.redacted);
  const sizeKB = new Blob([jsonString]).size / 1024;

  if (sizeKB > maxSizeKB) {
    // Truncate the JSON string
    const maxBytes = maxSizeKB * 1024;
    const truncated = jsonString.slice(0, maxBytes);
    const excessKB = sizeKB - maxSizeKB;

    try {
      // Try to parse truncated JSON, fallback to string if invalid
      const truncatedObj = JSON.parse(truncated);
      result.redacted = truncatedObj;
    } catch {
      result.redacted = {
        _truncated: true,
        _originalSizeKB: Math.round(sizeKB * 100) / 100,
        _excessKB: Math.round(excessKB * 100) / 100,
        content: truncated + `... (+${Math.round(excessKB * 100) / 100}KB)`
      };
    }
  }

  return result;
}

/**
 * Get feature flag for redaction from API (stage_tester_redaction_enabled)
 */
export async function getRedactionFlag(): Promise<boolean> {
  try {
    // Check environment variable first (for development)
    const envFlag = typeof window !== 'undefined' && (window as any).VITE_REDACTION_ENABLED;
    if (envFlag === 'true') return true;
    if (envFlag === 'false') return false;

    // Fetch from feature flags API
    const response = await fetch('/api/demo/feature-flags', {
      method: 'GET',
      credentials: 'include',
      // No Content-Type header for GET requests (preflight-safe)
    });

    if (!response.ok) {
      console.warn('Failed to fetch feature flags, defaulting to enabled');
      return true; // Default ON as per feedback
    }

    const flags = await response.json();
    return flags.stage_tester_redaction_enabled !== false; // Default ON unless explicitly disabled
  } catch (error) {
    console.warn('Error fetching redaction flag:', error);
    return true; // Default ON on error
  }
}

/**
 * Enhanced redaction with transport secrets ALWAYS masked regardless of feature flag
 */
export function redactWithTransportSecurityFirst(
  content: any,
  redactionEnabled: boolean = true
): RedactionResult {
  // ALWAYS apply transport-level security (secrets, keys, tokens)
  const transportSecurityResult = redactContent(content, {
    enabled: true, // Always enabled for transport security
    preserveLength: false
  });

  // If feature flag is disabled, return only transport security redaction
  if (!redactionEnabled) {
    return {
      ...transportSecurityResult,
      redactionEnabled: false
    };
  }

  // If feature flag is enabled, apply additional PII/content redaction
  return redactContent(transportSecurityResult.redacted, {
    enabled: true,
    preserveLength: true
  });
}

/**
 * Check if a value looks like a secret (heuristic)
 */
export function looksLikeSecret(value: string): boolean {
  if (typeof value !== 'string' || value.length < 8) {
    return false;
  }

  // Common secret patterns
  const secretPatterns = [
    /^[A-Za-z0-9+/]{40,}={0,2}$/, // Base64-like
    /^[A-Fa-f0-9]{32,}$/, // Hex tokens
    /^sk-[A-Za-z0-9]{48,}$/, // OpenAI-style keys
    /^xoxb-[A-Za-z0-9-]{50,}$/, // Slack tokens
    /^ghp_[A-Za-z0-9]{36}$/, // GitHub personal access tokens
  ];

  return secretPatterns.some(pattern => pattern.test(value));
}

/**
 * Redact user ID for privacy (always redact last 4 characters)
 */
export function redactUserId(userId: string): string {
  if (!userId || userId.length <= 4) {
    return '[REDACTED]';
  }
  return userId.slice(0, -4) + '****';
}

/**
 * Format duration in milliseconds to readable format
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  if (ms < 60000) {
    return `${Math.round(ms / 1000 * 10) / 10}s`;
  }
  return `${Math.round(ms / 60000 * 10) / 10}m`;
}

/**
 * Format timestamp for logs (ISO with milliseconds)
 */
export function formatTimestamp(date: Date = new Date()): string {
  return date.toISOString().replace('T', ' ').slice(0, -1);
}

/**
 * Redact log entry based on feature flag
 */
export function redactLogEntry(
  entry: any,
  redactionEnabled: boolean = false
): any {
  const result = redactForLogging(entry, redactionEnabled);
  return result.redacted;
}

/**
 * Redact API call data (URL, headers, body)
 */
export function redactApiCall(
  call: { url?: string; headers?: any; body?: any },
  redactionEnabled: boolean = false
): any {
  const result = redactForLogging(call, redactionEnabled);
  return result.redacted;
}

/**
 * Redact stage state for sharing/copying
 */
export function redactStageState(
  state: any,
  redactionEnabled: boolean = false
): any {
  const result = redactContent(state, {
    enabled: redactionEnabled,
    preserveLength: true
  });
  return result.redacted;
}

/**
 * Redact prompt for copy operation
 */
export function redactPromptForCopy(
  prompt: any,
  redactionEnabled: boolean = false
): any {
  const result = redactContent(prompt, {
    enabled: redactionEnabled,
    preserveLength: false // Don't preserve length for copy operations
  });
  return result.redacted;
}

/**
 * DV-STAGE-004: Enhanced Prompt Redaction
 * Specialized redaction rules for prompt preview system
 */

export interface PromptRedactionOptions {
  enabled: boolean;
  showTTLs?: boolean; // Show TTL/length info for masked values
  showTypes?: boolean; // Show type info for masked values
  customRules?: readonly RedactionRule[];
}

export interface RedactionRule {
  pattern: RegExp | string;
  type: 'secret' | 'pii' | 'content';
  replacement?: string;
  description?: string;
}

export interface PromptRedactionResult extends RedactionResult {
  rules: RedactionRule[];
  maskedValues: Array<{
    key: string;
    type: 'secret' | 'pii' | 'content';
    originalLength: number;
    masked: string;
    ttl?: number;
  }>;
  stats: {
    secretsMasked: number;
    piiMasked: number;
    contentMasked: number;
    totalCharactersMasked: number;
  };
}

// Stage-specific allowlists to reduce false positives
const STAGE_ALLOWLISTS: Record<string, string[]> = {
  script: ['OpenAI', 'ChatGPT', 'GPT-4', 'Claude', 'Anthropic', 'script', 'narrative', 'educational'],
  vision: ['DALL-E', 'Midjourney', 'Stable Diffusion', 'visual', 'image', 'generate', 'create'],
  storyboard: ['shot', 'scene', 'frame', 'camera', 'angle', 'composition'],
  frames: ['render', 'frame', 'resolution', '1080p', '4K', 'aspect', 'ratio'],
  video: ['ffmpeg', 'encoding', 'fps', 'bitrate', 'codec', 'h264', 'mp4'],
  qc: ['quality', 'check', 'validation', 'review', 'approve', 'reject'],
  export: ['format', 'export', 'download', 'delivery', 'final'],
  music: ['audio', 'sound', 'track', 'melody', 'background', 'sync']
};

// Enhanced redaction rules with tokenization-aware patterns
const PROMPT_REDACTION_RULES: RedactionRule[] = [
  // Provider API keys and tokens (high confidence patterns)
  { pattern: /\bsk-[A-Za-z0-9]{48,}\b/, type: 'secret', replacement: 'sk-***[OPENAI_KEY]', description: 'OpenAI API key' },
  { pattern: /\bxoxb-[A-Za-z0-9\-]{50,}\b/, type: 'secret', replacement: 'xoxb-***[SLACK_TOKEN]', description: 'Slack bot token' },
  { pattern: /\bghp_[A-Za-z0-9]{36}\b/, type: 'secret', replacement: 'ghp_***[GITHUB_TOKEN]', description: 'GitHub personal access token' },
  { pattern: /\bAIza[A-Za-z0-9\-_]{35}\b/, type: 'secret', replacement: 'AIza***[GOOGLE_API_KEY]', description: 'Google API key' },
  { pattern: /\bAKIA[A-Z0-9]{16}\b/, type: 'secret', replacement: 'AKIA***[AWS_ACCESS_KEY]', description: 'AWS access key' },

  // Generic secrets (more conservative patterns with word boundaries)
  { pattern: /\b[A-Za-z0-9+/]{40,}={0,2}(?=\s|$)/, type: 'secret', replacement: '***[BASE64_SECRET]', description: 'Base64-encoded secret' },
  { pattern: /\b[A-Fa-f0-9]{32,}(?=\s|$)/, type: 'secret', replacement: '***[HEX_SECRET]', description: 'Hexadecimal secret' },

  // PII patterns (more precise)
  { pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/, type: 'pii', replacement: '***@example.com', description: 'Email address' },
  { pattern: /\b\d{3}-\d{2}-\d{4}\b/, type: 'pii', replacement: '***-**-****', description: 'SSN' },
  { pattern: /\b\d{3}[.-]\d{3}[.-]\d{4}\b/, type: 'pii', replacement: '***-***-****', description: 'Phone number' },
  { pattern: /\b(?:\d{4}[- ]?){3}\d{4}\b/, type: 'pii', replacement: '****-****-****-****', description: 'Credit card number' },

  // IP addresses (excluding common ranges like 192.168.x.x examples)
  { pattern: /\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/, type: 'pii', replacement: '192.168.1.***', description: 'IPv4 address' }
];

/**
 * Tokenize text for more accurate pattern matching
 */
function tokenizeForRedaction(text: string): string[] {
  // Simple tokenization - split on whitespace and punctuation but keep structure
  return text.split(/(\s+|[^\w\s])/);
}

/**
 * Check if a token should be skipped based on stage allowlist
 */
function isTokenAllowlisted(token: string, stage?: string): boolean {
  if (!stage || !STAGE_ALLOWLISTS[stage]) return false;

  const allowlist = STAGE_ALLOWLISTS[stage];
  return allowlist.some(allowed =>
    token.toLowerCase().includes(allowed.toLowerCase()) ||
    allowed.toLowerCase().includes(token.toLowerCase())
  );
}

/**
 * Advanced prompt redaction with tokenization and allowlist filtering
 */
export function redactPromptAdvanced(
  promptText: string,
  options: PromptRedactionOptions & { stage?: string } = { enabled: false }
): PromptRedactionResult {
  if (!options.enabled) {
    return {
      redacted: promptText,
      maskedKeys: [],
      redactionEnabled: false,
      rules: [],
      maskedValues: [],
      stats: { secretsMasked: 0, piiMasked: 0, contentMasked: 0, totalCharactersMasked: 0 }
    };
  }

  let redactedText = promptText;
  const maskedValues: Array<any> = [];
  const stats = { secretsMasked: 0, piiMasked: 0, contentMasked: 0, totalCharactersMasked: 0 };
  const appliedRules: RedactionRule[] = [];

  // Tokenize text for more accurate matching
  tokenizeForRedaction(promptText);

  // Combine default rules with custom rules
  const allRules = [...PROMPT_REDACTION_RULES, ...(options.customRules || [])];

  for (const rule of allRules) {
    const pattern = typeof rule.pattern === 'string' ? new RegExp(rule.pattern, 'g') : new RegExp(rule.pattern.source, 'g');

    // Find matches but validate against allowlist
    let match;
    while ((match = pattern.exec(redactedText)) !== null) {
      const matchedText = match[0];

      // Skip if token is allowlisted for this stage (reduces false positives)
      if (rule.type !== 'secret' && options.stage && isTokenAllowlisted(matchedText, options.stage)) {
        continue;
      }

      // Skip very common false positives
      if (rule.type === 'pii' && /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(matchedText)) {
        continue; // Skip private IP ranges that are commonly used in examples
      }

      const originalLength = matchedText.length;
      let replacement = rule.replacement || `***[${rule.type.toUpperCase()}]`;

      // Add length/TTL info if requested
      if (options.showTTLs && originalLength > 10) {
        replacement += `(${originalLength}ch)`;
      }

      if (options.showTypes) {
        replacement += `[${rule.type}]`;
      }

      maskedValues.push({
        key: `pattern_${rule.type}_${maskedValues.length}`,
        type: rule.type,
        originalLength,
        masked: replacement,
        ttl: rule.type === 'secret' ? 3600 : undefined // 1 hour for secrets
      });

      // Update stats
      stats.totalCharactersMasked += originalLength;
      if (rule.type === 'secret') stats.secretsMasked++;
      else if (rule.type === 'pii') stats.piiMasked++;
      else if (rule.type === 'content') stats.contentMasked++;

      // Replace in text
      redactedText = redactedText.replace(matchedText, replacement);

      if (!appliedRules.includes(rule)) {
        appliedRules.push(rule);
      }
    }
  }

  return {
    redacted: redactedText,
    maskedKeys: maskedValues.map(v => v.key),
    redactionEnabled: true,
    rules: appliedRules,
    maskedValues,
    stats
  };
}

/**
 * Redact prompt template variables before rendering
 */
export function redactPromptVariables(
  variables: Record<string, any>,
  options: PromptRedactionOptions = { enabled: false }
): { redacted: Record<string, unknown>; maskedKeys: string[] } {
  if (!options.enabled) {
    return { redacted: variables, maskedKeys: [] };
  }

  const redacted: Record<string, unknown> = {};
  const maskedKeys: string[] = [];

  for (const [key, value] of Object.entries(variables)) {
    // Always mask secret keys
    if (isSecretKey(key)) {
      redacted[key] = maskValue(value, false);
      maskedKeys.push(key);
      continue;
    }

    // Redact content based on key patterns
    if (isRedactableKey(key)) {
      redacted[key] = maskValue(value, true);
      maskedKeys.push(key);
      continue;
    }

    // Check value patterns for secrets/PII
    if (typeof value === 'string') {
      const result = redactPromptAdvanced(value, options);
      if (result.maskedValues.length > 0) {
        redacted[key] = result.redacted;
        maskedKeys.push(key);
      } else {
        redacted[key] = value;
      }
    } else {
      redacted[key] = value;
    }
  }

  return { redacted, maskedKeys };
}

/**
 * Check if a prompt contains sensitive content
 */
export function containsSensitiveContent(promptText: string): {
  hasSensitive: boolean;
  types: Array<'secret' | 'pii' | 'content'>;
  matches: Array<{ type: string; pattern: string; description: string }>;
} {
  const types: Array<'secret' | 'pii' | 'content'> = [];
  const matches: Array<any> = [];

  for (const rule of PROMPT_REDACTION_RULES) {
    const pattern = typeof rule.pattern === 'string' ? new RegExp(rule.pattern, 'g') : rule.pattern;

    if (pattern.test(promptText)) {
      if (!types.includes(rule.type)) {
        types.push(rule.type);
      }
      matches.push({
        type: rule.type,
        pattern: rule.pattern.toString(),
        description: rule.description || `${rule.type} pattern`
      });
    }
  }

  return {
    hasSensitive: types.length > 0,
    types,
    matches
  };
}

/**
 * Get redaction summary for UI display
 */
export function getRedactionSummary(
  result: PromptRedactionResult
): {
  totalMasked: number;
  byType: Record<string, number>;
  charactersReduced: number;
  hasHighRiskSecrets: boolean;
} {
  const byType: Record<string, number> = {};
  let charactersReduced = 0;

  for (const masked of result.maskedValues) {
    byType[masked.type] = (byType[masked.type] || 0) + 1;
    charactersReduced += masked.originalLength - masked.masked.length;
  }

  return {
    totalMasked: result.maskedValues.length,
    byType,
    charactersReduced: Math.max(0, charactersReduced),
    hasHighRiskSecrets: result.stats.secretsMasked > 0
  };
}

/**
 * Create redaction preset configurations
 */
export const REDACTION_PRESETS = {
  strict: {
    enabled: true,
    showTTLs: true,
    showTypes: true,
    customRules: []
  },

  moderate: {
    enabled: true,
    showTTLs: false,
    showTypes: false,
    customRules: []
  },

  minimal: {
    enabled: true,
    showTTLs: false,
    showTypes: false,
    customRules: PROMPT_REDACTION_RULES.filter(rule => rule.type === 'secret')
  },

  disabled: {
    enabled: false,
    showTTLs: false,
    showTypes: false,
    customRules: []
  }
} as const;

// Type exports handled inline above